import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import DonutChart from '../../components/Charts/DonutChart'
import BarChart from '../../components/Charts/BarChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import { createChartExportHandlers, createMenuToggle, setupMenuClickOutside, exportToCSV } from '../../utils/exportUtils'
import './HuddleObservationOverview.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

const ROLE_ORDER: string[] = [
    'Nurse',
    'Nurse Manager or charge Nurse',
    'Provider',
    'Doula',
    'Clinical educator or quality improvement',
    'Support person',
    'Other',
]

const ROLE_DISPLAY_LABEL: Record<string, string> = {
    'Nurse': 'Nurse',
    'Nurse Manager or charge Nurse': 'Nurse Manager or charge Nurse',
    'Provider': 'Provider (OB midwife pediatrician, etc)',
    'Doula': 'Doula',
    'Clinical educator or quality improvement': 'Clinical educator or quality improvement',
    'Support person': 'Support person (spouse, family, friend of patient)',
    'Other': 'Other',
}

const GRAND_TOTAL_KEY = 'Grand Total'
const HIGHLIGHTED_ROLE = 'Nurse Manager or charge Nurse'
const HDL_OBSERVATION_SLIDER_KEY = 'hdl_slider'
const HDL_LAST_HOSPITAL_COMBO_KEY = 'hdl_last_hospital_combo'

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const Q1_MENU_ID = 'hdl-q1-export-menu'
const Q2_MENU_ID = 'hdl-q2-export-menu'
const Q3_MENU_ID = 'hdl-q3-export-menu'

const Q1_QUESTION_TEXT = 'Q1 - Please attest to personally observing or being a part of a TeamBirth huddle by selecting your role in the huddle'
const Q1_PIE_TITLE = 'Type of Observer'
const Q2_QUESTION_TEXT = 'Q2 Which of the following Instances prompted the huddle? Select all that apply.'
const Q3_QUESTION_TEXT = 'Q3 Did the team clearly state that this conversation was a TeamBirth huddle?'

type Q3Bucket = 'Yes' | 'No' | 'Not Reported'

const Q3_RESPONSE_ORDER: Q3Bucket[] = ['Yes', 'No', 'Not Reported']

const Q3_BUCKET_LABELS: Record<Q3Bucket, string> = {
    'Yes': 'Yes- A team member stated clearly that a TeamBirth huddle was Occurring',
    'No': 'No- The huddle occurred but was not clearly named for those present',
    'Not Reported': 'Not Reported',
}

const Q3_HIGHLIGHTED_BUCKET: Q3Bucket = 'No'

const mapQ3Response = (raw: string): Q3Bucket | null => {
    const s = raw.trim().toLowerCase()
    if (!s || s === 'grand total' || s.startsWith('other')) return null
    if (s.startsWith('yes')) return 'Yes'
    if (s.startsWith('no response') || s.startsWith('not reported')) return 'Not Reported'
    if (s.startsWith('no')) return 'No'
    return null
}

const renderWithHuddleHighlight = (text: string): React.ReactNode => {
    const parts = text.split(/(huddle)/gi)
    return parts.map((part, i) =>
        part.toLowerCase() === 'huddle'
            ? <mark key={i} className="hdl-huddle-mark">{part}</mark>
            : <React.Fragment key={i}>{part}</React.Fragment>
    )
}

const OBSERVER_ORDER: string[] = [
    'Clinical Team Member',
    'Observer',
    'Support Person',
]

const OBSERVER_COLORS = ['#c66736', '#429c75', '#F9E15B']

const INITIATION_ORDER: string[] = [
    'Admission',
    'Labour Progress',
    'Pain Control',
    'Change in Care Management',
    'Discharge',
    'Prior to Non-Emergent Intervention for Birthing Person',
    'Prior to Non-Emergent Intervention for Baby',
    'Post-Birth/Post-Emergency Debrief',
    'Others',
]

const INITIATION_DISPLAY_LABEL: Record<string, string> = {
    'Labour Progress': 'Labor Progress',
    'Post-Birth/Post-Emergency Debrief': 'Post-Birth / Post-Emergency Debrief',
    'Others': 'Other',
}

const INITIATION_BAR_COLOR = '#87BDE4'

const HuddleObservationOverview: React.FC = () => {
    const { selectedHospitalIds } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data, loading, initialLoading, error } = useHospitalData<ZohoResponse>(
        '/api/hdl/observation_completed_role',
        hospitalIds
    )

    const {
        data: observerData,
        loading: observerLoading,
        initialLoading: observerInitialLoading,
        error: observerError,
    } = useHospitalData<ZohoResponse>(
        '/api/hdl/observation_q1_type_observer',
        hospitalIds
    )

    const {
        data: initData,
        loading: initLoading,
        initialLoading: initInitialLoading,
        error: initError,
    } = useHospitalData<ZohoResponse>(
        '/api/hdl/huddle_initiation_count',
        hospitalIds
    )

    const {
        data: statedData,
        loading: statedLoading,
        initialLoading: statedInitialLoading,
        error: statedError,
    } = useHospitalData<ZohoResponse>(
        '/api/hdl/huddle_stated_count',
        hospitalIds
    )


    const q1SectionRef = useRef<HTMLDivElement>(null)
    const q1Exports = useMemo(
        () => createChartExportHandlers(
            q1SectionRef,
            'Huddle Observation Completed By Role',
            'huddle_observation_completed_role',
            { fontBoost: true, exportTitle: 'Huddle Observation – Overview', exportTitleAlign: 'center', exportTitleFontSize: '28px', extraHideSelectors: ['.stacked-bar-chart-legend'] }
        ),
        []
    )
    const toggleQ1Menu = useMemo(() => createMenuToggle(Q1_MENU_ID), [])

    const q2SectionRef = useRef<HTMLDivElement>(null)
    const q2Exports = useMemo(
        () => createChartExportHandlers(
            q2SectionRef,
            'Huddle Initiation',
            'huddle_initiation_count'
        ),
        []
    )
    const toggleQ2Menu = useMemo(() => createMenuToggle(Q2_MENU_ID), [])

    const q3SectionRef = useRef<HTMLDivElement>(null)
    const q3Exports = useMemo(
        () => createChartExportHandlers(
            q3SectionRef,
            'Huddle Stated',
            'huddle_stated_count'
        ),
        []
    )
    const toggleQ3Menu = useMemo(() => createMenuToggle(Q3_MENU_ID), [])


    useEffect(() => {
        return setupMenuClickOutside(() => q1Exports.closeMenu(Q1_MENU_ID))
    }, [q1Exports])

    useEffect(() => {
        return setupMenuClickOutside(() => q2Exports.closeMenu(Q2_MENU_ID))
    }, [q2Exports])

    useEffect(() => {
        return setupMenuClickOutside(() => q3Exports.closeMenu(Q3_MENU_ID))
    }, [q3Exports])

    const rows: any[][] = data?.response?.result?.rows || []
    const observerRows: any[][] = observerData?.response?.result?.rows || []
    const initRows: any[][] = initData?.response?.result?.rows || []
    const statedRows: any[][] = statedData?.response?.result?.rows || []

    const monthList: MonthInfo[] = useMemo(() => {
        const allRows = [...rows, ...observerRows, ...initRows, ...statedRows]
        if (!allRows.length) return []
        return generateMonthList(allRows, 2)
    }, [rows, observerRows, initRows, statedRows])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_OBSERVATION_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}
            userInteractedRef.current = sliderRangeValues[0] !== 0 && sliderRangeValues[1] !== 0
        } else {
            userInteractedRef.current = false
            try {
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}
        }
    }

    const isLoadingData = loading || observerLoading || initLoading

    useEffect(() => {
        if (!monthList.length) return
        if (isLoadingData) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, isLoadingData, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    const aggregated = useMemo(() => {
        const roleCounts: Record<string, number> = {}
        let grandTotal = 0

        if (!monthList.length || !rows.length) {
            return { roleCounts, grandTotal }
        }

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        rows.forEach(r => {
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return

            const role = String(r[3] || '').trim()
            const count = Number(r[4]) || 0
            if (!role) return

            if (role === GRAND_TOTAL_KEY) {
                grandTotal += count
            } else {
                roleCounts[role] = (roleCounts[role] || 0) + count
            }
        })

        return { roleCounts, grandTotal }
    }, [rows, monthList, startIdx, endIdx])

    const observerAggregated = useMemo(() => {
        const counts: Record<string, number> = {}
        let total = 0

        if (!monthList.length || !observerRows.length) return { counts, total }

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        observerRows.forEach(r => {
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const role = String(r[3] || '').trim()
            const numerator = Number(r[4]) || 0
            if (!role) return
            counts[role] = (counts[role] || 0) + numerator
            total += numerator
        })

        return { counts, total }
    }, [observerRows, monthList, startIdx, endIdx])

    const initiationAggregated = useMemo(() => {
        const counts: Record<string, number> = {}
        let total = 0

        if (!monthList.length || !initRows.length) return { counts, total }

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        initRows.forEach(r => {
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const label = String(r[3] || '').trim()
            const count = Number(r[4]) || 0
            if (!label || label === GRAND_TOTAL_KEY) return
            counts[label] = (counts[label] || 0) + count
            total += count
        })

        return { counts, total }
    }, [initRows, monthList, startIdx, endIdx])

    const statedAggregated = useMemo(() => {
        const counts: Record<Q3Bucket, number> = { 'Yes': 0, 'No': 0, 'Not Reported': 0 }
        const present: Record<Q3Bucket, boolean> = { 'Yes': false, 'No': false, 'Not Reported': false }
        let grandTotal = 0
        let anyRow = false

        if (!monthList.length || !statedRows.length) return { counts, present, grandTotal, anyRow }

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        statedRows.forEach(r => {
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const raw = String(r[3] || '').trim()
            if (!raw) return

            const bucket = mapQ3Response(raw)
            if (!bucket) return

            const count = Number(r[4]) || 0
            counts[bucket] += count
            present[bucket] = true
            anyRow = true
            grandTotal += count
        })

        return { counts, present, grandTotal, anyRow }
    }, [statedRows, monthList, startIdx, endIdx])

    const statedHasData = statedAggregated.anyRow

    const formatStatedCount = (bucket: Q3Bucket): string =>
        statedAggregated.present[bucket] ? String(statedAggregated.counts[bucket]) : ''


    const pieSeries = OBSERVER_ORDER.map(role => observerAggregated.counts[role] || 0)
    const pieHasData = pieSeries.some(v => v > 0)

    const initiationCounts = INITIATION_ORDER.map(label => initiationAggregated.counts[label] || 0)
    const initiationHasData = initiationCounts.some(v => v > 0)

    const formatCount = (n: number | undefined): string => {
        return String(n || 0)
    }

    const handleDownloadQ1CSV = () => {
        const tableRows: (string | number)[][] = ROLE_ORDER.map(role => [
            'Huddle Observation Completed By Role',
            ROLE_DISPLAY_LABEL[role] || role,
            aggregated.roleCounts[role] || 0,
            '',
        ])
        tableRows.push(['Huddle Observation Completed By Role', 'Grand Total', aggregated.grandTotal || 0, ''])
        const observerTotal = observerAggregated.total || 0
        const pieCsvRows: (string | number)[][] = OBSERVER_ORDER.map(role => {
            const count = observerAggregated.counts[role] || 0
            const percent = observerTotal > 0 ? `${Math.round((count / observerTotal) * 100)}%` : ''
            return [Q1_QUESTION_TEXT, role, count, percent]
        })
        exportToCSV({
            headers: ['Section', 'Category', 'Count', 'Percentage'],
            rows: [...tableRows, ...pieCsvRows],
            filename: 'huddle_observation_completed_role',
        })
    }

    const handleDownloadQ2CSV = () => {
        const csvRows: (string | number)[][] = INITIATION_ORDER.map(label => [
            Q2_QUESTION_TEXT,
            INITIATION_DISPLAY_LABEL[label] || label,
            initiationAggregated.counts[label] || 0,
        ])
        csvRows.push([Q2_QUESTION_TEXT, 'Total', initiationAggregated.total || 0])
        exportToCSV({
            headers: ['Section', 'Category', 'Count'],
            rows: csvRows,
            filename: 'huddle_initiation_count',
        })
    }

    const handleDownloadQ3CSV = () => {
        const csvRows: (string | number)[][] = Q3_RESPONSE_ORDER.map(bucket => [
            Q3_QUESTION_TEXT,
            Q3_BUCKET_LABELS[bucket],
            statedAggregated.counts[bucket] || 0,
        ])
        csvRows.push([Q3_QUESTION_TEXT, 'Grand Total', statedAggregated.grandTotal || 0])
        exportToCSV({
            headers: ['Section', 'Response', 'Count'],
            rows: csvRows,
            filename: 'huddle_stated_count',
        })
    }

    const showInitialLoading = (initialLoading && loading) || (observerInitialLoading && observerLoading) || (initInitialLoading && initLoading) || (statedInitialLoading && statedLoading)
    const isRefreshing = (!initialLoading && loading) || (!observerInitialLoading && observerLoading) || (!initInitialLoading && initLoading) || (!statedInitialLoading && statedLoading)
    const hasHospitals = hospitalIds.length > 0
    const combinedError = error || observerError || initError || statedError
    const noData = !initialLoading && !loading && !observerInitialLoading && !observerLoading && !initInitialLoading && !initLoading && !statedInitialLoading && !statedLoading && !monthList.length

    return (
        <div className="hdl-observation-overview">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Huddle Observation – Overview</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !combinedError && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading huddle observation data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && combinedError && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {combinedError}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !combinedError && noData} />

                {hasHospitals && !combinedError && !noData && (
                    <div className="hdl-role-section" ref={q1SectionRef}>
                        <div className="hdl-role-section-head-row">
                            <div className="hdl-role-section-header">Huddle Observation Completed By Role</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleQ1Menu}>&#9776;</button>
                                <div id={Q1_MENU_ID} className="export-dropdown">
                                    <div onClick={() => q1Exports.handleDownloadSVG(Q1_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => q1Exports.handleDownloadPNG(Q1_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadQ1CSV(); q1Exports.closeMenu(Q1_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <div className="hdl-content-grid">
                            <div className="hdl-role-table-wrap">
                                <table className="hdl-role-table">
                                    <tbody>
                                        {ROLE_ORDER.map(role => (
                                            <tr key={role} className={role === HIGHLIGHTED_ROLE ? 'hdl-row-highlighted' : undefined}>
                                                <td className="role-cell">{ROLE_DISPLAY_LABEL[role] || role}</td>
                                                <td className="count-cell">{formatCount(aggregated.roleCounts[role])}</td>
                                            </tr>
                                        ))}
                                        <tr className="grand-total-row">
                                            <td className="role-cell">Grand Total</td>
                                            <td className="count-cell">{formatCount(aggregated.grandTotal)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="hdl-pie-section">
                                <div className="hdl-q1-label">{Q1_PIE_TITLE}</div>
                                {pieHasData ? (
                                    <DonutChart
                                        labels={OBSERVER_ORDER}
                                        series={pieSeries}
                                        tooltipCounts={pieSeries}
                                        colors={OBSERVER_COLORS}
                                        height={280}
                                        type="pie"
                                        legendPosition="right"
                                        showLegend={true}
                                        legendLineHeight={1}
                                        exportFilename="huddle_observation_q1_type_observer"
                                    />
                                ) : (
                                    <div className="hdl-pie-empty">No data for the selected range</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {hasHospitals && !combinedError && !noData && (
                    <div className="hdl-init-section" ref={q2SectionRef}>
                        <div className="hdl-init-head-row">
                            <div className="hdl-role-section-header">Huddle Initiation</div>
                            <div className="hdl-init-question-label">{Q2_QUESTION_TEXT}</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleQ2Menu}>&#9776;</button>
                                <div id={Q2_MENU_ID} className="export-dropdown">
                                    <div onClick={() => q2Exports.handleDownloadSVG(Q2_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => q2Exports.handleDownloadPNG(Q2_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadQ2CSV(); q2Exports.closeMenu(Q2_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <div className="hdl-init-bar-wrap">
                            {initiationHasData ? (
                                <BarChart
                                    categories={INITIATION_ORDER.map(k => INITIATION_DISPLAY_LABEL[k] || k)}
                                    series={[{ name: 'Count', data: initiationCounts }]}
                                    horizontal={true}
                                    colors={[INITIATION_BAR_COLOR]}
                                    height={280}
                                    fontFamily='"Poppins", "Segoe UI", sans-serif'
                                    showLegend={false}
                                    showToolbar={false}
                                    showGrid={false}
                                    xAxisShow={false}
                                    xAxisReserveSpace={true}
                                    showXAxisBorder={false}
                                    yAxisShow={true}
                                    yAxisWidth={500}
                                    dataLabelsEnabled={true}
                                    dataLabelsOffsetX={18}
                                    dataLabelsColor={['#1f2937']}
                                    dataLabelsFontWeight="500"
                                    dataLabelsFontSize="13px"
                                    decimals={0}
                                    plotOptionsBar={{
                                        horizontal: true,
                                        barHeight: '55%',
                                        borderRadius: 0,
                                        dataLabels: { position: 'top' },
                                    }}
                                    gridPadding={{ right: 30, left: 15, top: 0, bottom: 10 }}
                                    xAxisMax={(() => {
                                        const peak = Math.max(...initiationCounts, 0)
                                        if (peak <= 0) return 5
                                        const padded = peak * 1.15
                                        const step = peak < 20 ? 5 : peak < 100 ? 10 : 25
                                        return Math.ceil(padded / step) * step
                                    })()}
                                    exportFilename="huddle_initiation_count"
                                />
                            ) : (
                                <div className="hdl-pie-empty">No data for the selected range</div>
                            )}
                        </div>
                    </div>
                )}

                {hasHospitals && !combinedError && !noData && (
                    <div className="hdl-stated-section" ref={q3SectionRef}>
                        <div className="hdl-stated-head-row">
                            <div className="hdl-role-section-header">Huddle Stated</div>
                            <div className="hdl-stated-question-label">{Q3_QUESTION_TEXT}</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleQ3Menu}>&#9776;</button>
                                <div id={Q3_MENU_ID} className="export-dropdown">
                                    <div onClick={() => q3Exports.handleDownloadSVG(Q3_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => q3Exports.handleDownloadPNG(Q3_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadQ3CSV(); q3Exports.closeMenu(Q3_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <div className="hdl-stated-table-wrap">
                            {statedHasData ? (
                                <table className="hdl-stated-table">
                                    <tbody>
                                        {Q3_RESPONSE_ORDER.map(bucket => {
                                            const isHighlighted = bucket === Q3_HIGHLIGHTED_BUCKET
                                            return (
                                                <tr
                                                    key={bucket}
                                                    className={isHighlighted ? 'hdl-stated-row-highlighted' : undefined}
                                                >
                                                    <td className="response-cell">{renderWithHuddleHighlight(Q3_BUCKET_LABELS[bucket])}</td>
                                                    <td className="count-cell">{formatStatedCount(bucket)}</td>
                                                </tr>
                                            )
                                        })}
                                        <tr className="grand-total-row">
                                            <td className="response-cell">Grand Total</td>
                                            <td className="count-cell">{statedAggregated.anyRow ? String(statedAggregated.grandTotal) : ''}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <div className="hdl-pie-empty">No data for the selected range</div>
                            )}
                        </div>
                    </div>
                )}

                {isRefreshing && (
                    <div className="chart-loading-overlay">
                        <div>Loading...</div>
                    </div>
                )}

            </div>
        </div>
    )
}

export default HuddleObservationOverview
